import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import { queryKeys } from "./keys";
import type {
  CreateReservoirInput,
  ListResponse,
  Reservoir,
  UpdateReservoirInput,
} from "./types";

const base = "/v1/reservoirs";

export interface ListReservoirsParams {
  limit?: number;
  offset?: number;
}

export function useReservoirs(params: ListReservoirsParams = {}) {
  return useQuery({
    queryKey: queryKeys.reservoirs.list(params as Record<string, unknown>),
    queryFn: ({ signal }) =>
      api.get<ListResponse<Reservoir>>(
        base,
        { limit: params.limit, offset: params.offset },
        signal,
      ),
  });
}

export function useReservoir(id: string | undefined) {
  return useQuery({
    queryKey: id ? queryKeys.reservoirs.detail(id) : queryKeys.reservoirs.all,
    queryFn: ({ signal }) => api.get<Reservoir>(`${base}/${id}`, undefined, signal),
    enabled: !!id,
  });
}

export function useCreateReservoir() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReservoirInput) =>
      api.post<Reservoir>(base, input),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: queryKeys.reservoirs.all });
      qc.setQueryData(queryKeys.reservoirs.detail(created.id), created);
    },
  });
}

export function useUpdateReservoir(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateReservoirInput) =>
      api.patch<Reservoir>(`${base}/${id}`, input),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.reservoirs.detail(id), updated);
      qc.invalidateQueries({ queryKey: queryKeys.reservoirs.all });
    },
  });
}

export function useArchiveReservoir(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string | undefined) =>
      api.delete<void>(`${base}/${id}`, { reason: reason ?? "" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.reservoirs.all });
    },
  });
}
